import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

import { useCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { Org } from '@/shared/types/api';

type Props = UseQueryOptions<Org | null, unknown, Org | null>;

export const getCurrentOrgQueryKey = ({
  orgId,
  orgs,
}: {
  orgId?: number | null;
  orgs?: Org[];
}) => {
  return ['current-org', orgId, orgs];
};

export const useCurrentOrg = (props?: Props) => {
  const currentOrgId = useCurrentOrgId();
  const { data: orgs, isLoading: isOrgsLoading } = useUserOrgs();

  return useQuery({
    ...props,
    queryKey: getCurrentOrgQueryKey({ orgId: currentOrgId, orgs }),
    queryFn: async () => {
      if (!orgs?.length) return null;

      // If no org ID is selected but we have orgs, use the first one
      if (!currentOrgId && orgs.length > 0) {
        return orgs[0];
      }

      // Find the org that matches the selected ID
      return orgs.find((org) => org.id === currentOrgId) || orgs[0];
    },
    enabled: !isOrgsLoading && !!orgs,
    placeholderData: keepPreviousData,
  });
};
